import { useCallback, useMemo, useState } from 'react';
import DataTable, { Column } from '@/components/common/DataTable';
import ReciboDetailModal from '@/components/recibos/ReciboDetailModal';
import { useDialog } from '@/hooks/useDialog';
import reciboService from '@/services/reciboService';
import { Recibo, TipoPagoRecibo } from '@/types/recibo';
import { formatCurrency, formatReceiptAmount } from '@/utils/currencyUtils';

interface ReciboTableProps {
  receipts: Recibo[];
  loading: boolean;
  emptyState: React.ReactNode;
  reportType: TipoPagoRecibo;
  onReceiptUpdated?: () => void;
}

const formatAmount = (receipt: Recibo, amount?: number | null, currency = receipt.moneda) =>
  amount === null || amount === undefined ? '—' : formatCurrency(Number(amount), currency);

const commonColumns: Column<Recibo>[] = [
  {
    key: 'numero_recibo',
    label: 'Número',
    sortable: true,
    className: 'whitespace-nowrap',
    render: (receipt) => <span className="font-semibold text-gray-950">{receipt.numero_recibo}</span>,
  },
  {
    key: 'numero_contrato',
    label: 'Contrato',
    sortable: true,
    className: 'whitespace-nowrap',
    render: (receipt) => receipt.numero_contrato || receipt.contrato_id || '-',
  },
];

export default function ReciboTable({
  receipts,
  loading,
  emptyState,
  reportType,
  onReceiptUpdated,
}: ReciboTableProps) {
  const { showAlert } = useDialog();
  const [selectedReceipt, setSelectedReceipt] = useState<Recibo | null>(null);

  const openReceipt = useCallback(async (receipt: Recibo) => {
    try {
      const response = await reciboService.getReciboById(receipt.id);
      setSelectedReceipt((response as any).data || response);
    } catch (error) {
      console.error('Error fetching receipt details:', error);
      showAlert({
        type: 'error',
        title: 'Error',
        message: 'Error al cargar los detalles del recibo',
      });
    }
  }, [showAlert]);

  const columns = useMemo<Column<Recibo>[]>(() => {
    if (reportType === TipoPagoRecibo.PROPIETARIO) {
      return [
        ...commonColumns,
        {
          key: 'propietario_nombre',
          label: 'Propietario',
          sortable: true,
          render: (receipt) => receipt.propietario_nombre || '—',
        },
        {
          key: 'alquiler',
          label: 'Alquiler',
          sortable: true,
          className: 'whitespace-nowrap text-right',
          render: (receipt) => formatAmount(receipt, receipt.alquiler),
        },
        {
          key: 'comision',
          label: 'Comisión',
          sortable: true,
          className: 'whitespace-nowrap text-right',
          render: (receipt) => formatAmount(receipt, receipt.comision),
        },
        {
          key: 'iva',
          label: 'IVA',
          sortable: true,
          className: 'whitespace-nowrap text-right',
          render: (receipt) => formatAmount(receipt, receipt.iva),
        },
        {
          key: 'impuestos',
          label: 'Impuestos',
          sortable: true,
          className: 'whitespace-nowrap text-right',
          render: (receipt) => formatAmount(receipt, receipt.impuestos ?? 0),
        },
        {
          key: 'importe',
          label: 'Importe',
          sortable: true,
          className: 'whitespace-nowrap text-right',
          render: (receipt) => formatReceiptAmount(Number(receipt.importe ?? 0), receipt.moneda),
        },
      ];
    }

    return [
      ...commonColumns,
      {
        key: 'inquilino_nombre',
        label: 'Inquilino',
        sortable: true,
        render: (receipt) => receipt.inquilino_nombre || '—',
      },
      {
        key: 'propietario_monto',
        label: 'Propietario',
        sortable: true,
        className: 'whitespace-nowrap text-right',
        render: (receipt) => formatAmount(receipt, receipt.propietario_monto, receipt.propietario_moneda ?? receipt.moneda),
      },
      {
        key: 'comision',
        label: 'Comisión',
        sortable: true,
        className: 'whitespace-nowrap text-right',
        render: (receipt) => formatAmount(receipt, receipt.comision, receipt.propietario_moneda ?? receipt.moneda),
      },
      {
        key: 'interes',
        label: 'Interés',
        sortable: true,
        className: 'whitespace-nowrap text-right',
        render: (receipt) => formatAmount(receipt, receipt.interes),
      },
      {
        key: 'iva',
        label: 'IVA',
        sortable: true,
        className: 'whitespace-nowrap text-right',
        render: (receipt) => formatAmount(receipt, receipt.iva, receipt.propietario_moneda ?? receipt.moneda),
      },
      {
        key: 'alquiler',
        label: 'Alquiler',
        sortable: true,
        className: 'whitespace-nowrap text-right',
        render: (receipt) => formatAmount(receipt, receipt.alquiler),
      },
    ];
  }, [reportType]);

  return (
    <>
      <DataTable
        columns={columns}
        data={receipts}
        loading={loading}
        searchable={false}
        onRowClick={openReceipt}
        rowKey="id"
        emptyState={emptyState}
        tableClassName={`${reportType === TipoPagoRecibo.INQUILINO ? 'min-w-[1050px]' : 'min-w-[980px]'} table-auto`}
      />

      {selectedReceipt ? (
        <ReciboDetailModal
          isOpen
          onClose={() => setSelectedReceipt(null)}
          recibo={selectedReceipt}
          onReciboUpdated={() => {
            setSelectedReceipt(null);
            onReceiptUpdated?.();
          }}
        />
      ) : null}
    </>
  );
}
